import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronRight, 
  Home, 
  BookOpen, 
  Route, 
  Target,
  CheckCircle,
  PlayCircle,
  Lock,
  Bookmark,
  Share2,
  Star
} from 'lucide-react';
import { PathNode, LearningPath } from '@/hooks/useLearningPaths';

interface LearningBreadcrumbsProps {
  // Context information
  disciplineName?: string;
  learningPath?: LearningPath;
  nodes?: PathNode[];
  trailType?: string;
  currentNode?: PathNode;
  currentIndex?: number;
  totalNodes?: number;
  
  // Progress information
  userProgress?: Record<string, { completed: boolean; timeSpent: number; rating?: number }>;
  
  // Navigation handlers
  onNavigateHome?: () => void;
  onNavigateToDiscipline?: () => void;
  onNavigateToPath?: () => void;
  onNavigateToNode?: (nodeId: string, index: number) => void;
  
  // Display options
  showProgress?: boolean;
  showNodePreview?: boolean;
  compact?: boolean;
}

export const LearningBreadcrumbs: React.FC<LearningBreadcrumbsProps> = ({
  disciplineName,
  learningPath,
  nodes = [],
  trailType,
  currentNode,
  currentIndex = 0,
  totalNodes = 0,
  userProgress = {},
  onNavigateHome,
  onNavigateToDiscipline,
  onNavigateToPath,
  onNavigateToNode,
  showProgress = true,
  showNodePreview = true,
  compact = false
}) => {
  const getNodeStatus = (nodeId: string, index: number) => {
    const progress = userProgress[nodeId];
    if (progress?.completed) return 'completed';
    
    // Check if prerequisites are met
    if (index > 0) {
      // For simplicity, assume sequential unlocking
      return 'locked';
    }
    
    return 'available';
  };

  const getStatusIcon = (status: string, size: string = 'h-3 w-3') => {
    switch (status) {
      case 'completed': return <CheckCircle className={`${size} text-green-600`} />;
      case 'available': return <PlayCircle className={`${size} text-black`} />;
      case 'locked': return <Lock className={`${size} text-gray-400`} />;
      default: return <PlayCircle className={`${size} text-gray-400`} />;
    }
  };

  const getTrailTypeColor = (trailType?: string) => {
    switch (trailType) {
      case 'historical': return 'bg-white text-black border-blue-300';
      case 'conceptual': return 'bg-white text-black border-purple-300';
      case 'methodological': return 'bg-white text-green-700 border-green-300';
      case 'application': return 'bg-white text-black border-gray-200';
      case 'foundational': return 'bg-white text-black border-gray-300';
      default: return 'bg-white text-black border-gray-300';
    }
  };

  // Calculate progress percentage
  const progressPercentage = totalNodes > 0 ? Math.round(((currentIndex + 1) / totalNodes) * 100) : 0;
  const completedNodes = Object.values(userProgress).filter(p => p.completed).length;

  // Contextual Actions
  const handleBookmark = () => {
    // TODO: Integrate with bookmark logic
    alert('Bookmark action!');
  };
  const handleShare = () => {
    // TODO: Integrate with share logic
    alert('Share action!');
  };
  const handleRate = () => {
    // TODO: Integrate with rating logic
    alert('Rate action!');
  };

  // Quick navigation
  const canGoPrev = currentIndex > 0 && nodes.length > 0;
  const canGoNext = nodes.length > 0 && currentIndex < (nodes.length - 1);
  const prevNode = canGoPrev ? nodes[currentIndex - 1] : undefined;
  const nextNode = canGoNext ? nodes[currentIndex + 1] : undefined;

  // Trail type badge
  const trailTypeValue = trailType || (learningPath && learningPath.metadata && learningPath.metadata.trail_type);

  return (
    <div className={`bg-white border-b ${compact ? 'py-2' : 'py-4'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center space-x-2 text-sm">
            {/* Home */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onNavigateHome}
              className="h-8 px-2 text-black hover:text-black"
            >
              <Home className="h-4 w-4" />
              {!compact && <span className="ml-1">Home</span>}
            </Button>

            {/* Discipline */}
            {disciplineName && (
              <>
                <ChevronRight className="h-3 w-3 text-gray-400" />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateToDiscipline}
                  className="h-8 px-2 text-black hover:text-black"
                >
                  <BookOpen className="h-4 w-4" />
                  {!compact && <span className="ml-1 truncate max-w-32">{disciplineName}</span>}
                </Button>
              </>
            )}

            {/* Learning Path */}
            {learningPath && (
              <>
                <ChevronRight className="h-3 w-3 text-gray-400" />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateToPath}
                  className="h-8 px-2 text-black hover:text-black"
                >
                  <Route className="h-4 w-4" />
                  {!compact && (
                    <span className="ml-1 truncate max-w-48">{learningPath.title}</span>
                  )}
                </Button>
              </>
            )}

            {/* Current Node */}
            {currentNode && (
              <>
                <ChevronRight className="h-3 w-3 text-gray-400" />
                <div className="flex items-center gap-2 px-2 py-1 bg-white rounded-md border border-blue-200">
                  {getStatusIcon(getNodeStatus(currentNode.node_id, currentIndex))}
                  <span className="font-medium text-blue-900 truncate max-w-48">
                    {currentNode.title || `Node ${currentIndex + 1}`}
                  </span>
                  {!compact && (
                    <Badge variant="outline" className="text-xs">
                      {currentIndex + 1}/{totalNodes}
                    </Badge>
                  )}
                </div>
              </>
            )}
          </nav>

          {/* Progress and Actions */}
          <div className="flex items-center gap-4">
            {/* Trail Type Badge */}
            {trailTypeValue && !compact && (
              <Badge variant="outline" className={getTrailTypeColor(trailTypeValue)}>
                {trailTypeValue}
              </Badge>
            )}

            {/* Progress Indicator */}
            {showProgress && totalNodes > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center gap-1 text-black">
                  <Target className="h-3 w-3" />
                  <span>{progressPercentage}%</span>
                </div>
                {!compact && (
                  <div className="w-20 bg-white rounded-full h-1.5">
                    <div 
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Completed Count */}
            {showProgress && !compact && (
              <div className="flex items-center gap-1 text-sm text-green-600">
                <CheckCircle className="h-3 w-3" />
                <span>{completedNodes} completed</span>
              </div>
            )}

            {/* Contextual Actions */}
            <div className="flex items-center gap-2 ml-2">
              <button onClick={handleBookmark} title="Bookmark" className="p-1 rounded hover:bg-white">
                <Bookmark className="h-4 w-4 text-black" />
              </button>
              <button onClick={handleShare} title="Share" className="p-1 rounded hover:bg-white">
                <Share2 className="h-4 w-4 text-black" />
              </button>
              <button onClick={handleRate} title="Rate" className="p-1 rounded hover:bg-white">
                <Star className="h-4 w-4 text-sky-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Quick Previous/Next Navigation */}
        {learningPath && currentNode && (
          <div className="flex items-center justify-between mt-2">
            <button
              disabled={!canGoPrev}
              onClick={() => canGoPrev && onNavigateToNode && prevNode && onNavigateToNode(prevNode.node_id, currentIndex - 1)}
              className={`px-3 py-1 rounded ${canGoPrev ? 'bg-white hover:bg-white text-black' : 'bg-white text-gray-400 cursor-not-allowed'}`}
            >
              &larr; Previous
            </button>
            <span className="text-xs text-black">
              Step {currentIndex + 1} of {totalNodes}
            </span>
            <button
              disabled={!canGoNext}
              onClick={() => canGoNext && onNavigateToNode && nextNode && onNavigateToNode(nextNode.node_id, currentIndex + 1)}
              className={`px-3 py-1 rounded ${canGoNext ? 'bg-white hover:bg-white text-black' : 'bg-white text-gray-400 cursor-not-allowed'}`}
            >
              Next &rarr;
            </button>
          </div>
        )}

        {/* Node Preview Bar - only shown when not compact and showNodePreview is true */}
        {!compact && showNodePreview && learningPath && currentNode && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-6 h-6 bg-blue-600 text-white rounded text-xs font-medium">
                  {currentIndex + 1}
                </div>
                <div>
                  <h4 className="font-medium text-black text-sm">
                    {currentNode.title || `Node ${currentIndex + 1}`}
                  </h4>
                  {currentNode.estimated_study_time && (
                    <p className="text-xs text-black">
                      Estimated time: {currentNode.estimated_study_time} minutes
                    </p>
                  )}
                </div>
              </div>

              {/* Quick Navigation */}
              <div className="flex items-center gap-1">
                {/* Previous node indicator */}
                {currentIndex > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onNavigateToNode?.(currentNode.node_id, currentIndex - 1)}
                    className="h-6 w-6 p-0"
                    title="Previous node"
                  >
                    <div className="w-2 h-2 bg-gray-400 rounded-full" />
                  </Button>
                )}

                {/* Current node indicator */}
                <div className="w-2 h-2 bg-blue-600 rounded-full ring-2 ring-blue-200" />

                {/* Next node indicator */}
                {currentIndex < totalNodes - 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onNavigateToNode?.(currentNode.node_id, currentIndex + 1)}
                    className="h-6 w-6 p-0"
                    title="Next node"
                  >
                    <div className="w-2 h-2 bg-gray-400 rounded-full" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};